import React, { useState, useEffect, useMemo } from 'react';
import { Job } from '../../views/jobs/JobTypes';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import { startOfWeek, addDays, format, startOfDay } from 'date-fns';
import { getDateFnsLocale } from 'internationalization/i18n';
import { useModal } from 'components/modals/ModalContext';
import WeekNavigation from 'components/layout/WeekNavigation';
import { saveData } from 'services/api/saveData';
import TimeInput from 'components/forms/basefields/TimeInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import JobModal from '../../views/jobs/JobModal';
import ProjectJobForm from '../../views/projects/ProjectJobForm';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { Project } from '../../views/projects/ProjectTypes';
import { DetailCardType } from 'types/DetailPageTypes';
import '../../style/scss/card.scss';

export interface ProjectPlanningTabProps extends DetailCardType {
    viewKey: string;
    project: Project;
    weekStartDate?: Date;
    refetchData?: () => void;
};

const ProjectPlanningTab: React.FC<ProjectPlanningTabProps> = ({ 
    project, weekStartDate, isEditable
}) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { userPreferences, userLocale } = useSettings();
    const [rows, setRows] = useState<Job[]>([]);
    const [projectDefaults, setProjectDefaults] = useState({
        default_start_time: project.default_start_time,
        default_end_time: project.default_end_time,
        default_break_duration: project.default_break_duration
    });
    const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => weekStartDate
        ? startOfWeek(new Date(weekStartDate), { weekStartsOn: 1 })
        : startOfWeek(new Date(), { weekStartsOn: 1 })
    );
    const [userHasOnlyViewRights] = useState<boolean>(useAllowedRight('only_view'));
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // Set the current rows
    useEffect(() => {
        if (project.jobs && project.jobs.length > 0) {
            setRows(project.jobs);
        }
    }, [project.jobs]);

    // Create a project job capacity map
    const projectJobCapacityMap = useMemo(() => {
        const map = new Map()

        // Loop through the rows to create the mapping
        rows.forEach(job => {
            job.project_job_capacities?.forEach(project_job_capacity => {
                map.set(`${job.id}-${project_job_capacity.date}`, project_job_capacity.required_employees)
            })
        })

        return map
    }, [rows]);

    // Format the current break duration value
    const formatBreakDuration = (value: string | number | null | undefined) => {
        if (value === null || value === undefined || value === "") return "";

        const hoursDecimal = Number(value);

        if (isNaN(hoursDecimal)) return "";

        const totalMinutes = Math.round(hoursDecimal * 60);

        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        return `${hours}:${String(minutes).padStart(2, '0')}`;
    }

    // Update the project defaults
    const updateProjectDefault = (field: string, value: string | null) => {
        setProjectDefaults((current) => ({ ...current, [field]: value }));
        setHasUnsavedChanges(true);
    };

    // Update the required employees
    const updateRequiredEmployees = (date: Date, value: string, jobId?: number | null) => {
        // Return if no job id is given
        if (!jobId) return

        // Get the number of employees or fallback to zero
        const employees = value === "" ? 0 : parseInt(value, 10)

        // Set unsaved changes to show save button
        setHasUnsavedChanges(true);

        // Convert the given date
        const dateKey = format(date, 'yyyy-MM-dd')

        // Update the project job capacity rows
        setRows(prevRows =>
            prevRows.map(job => {

                if (job.id !== jobId) return job

                const capacities = [...(job.project_job_capacities || [])]

                const existingIndex = capacities.findIndex(c => c.date === dateKey)

                if (existingIndex !== -1) {
                    capacities[existingIndex] = {
                        ...capacities[existingIndex],
                        required_employees: employees
                    }
                } else {
                    capacities.push({
                        date: dateKey,
                        required_employees: employees
                    } as any)
                }

                return {
                    ...job,
                    project_job_capacities: capacities
                }
            })
        )
    }

    // Handle save
    const handleSave = async () => {

        // Start the button loader
        setLoading(true);

        // Convert the default break duration to a decimal
        let breakDurationDecimal = null;
        if (projectDefaults.default_break_duration && projectDefaults.default_break_duration !== "") {
            const [hours, minutes] = projectDefaults.default_break_duration.split(':').map(Number);
            breakDurationDecimal = (hours * 60 + minutes) / 60;
        }

        // Save the project data
        await saveData({
            apiObject: 'project',
            itemId: project.id,
            data: {
                default_start_time: projectDefaults.default_start_time || null,
                default_end_time: projectDefaults.default_end_time || null,
                default_break_duration: breakDurationDecimal
            }
        });

        // Loop through the jobs of the rows to save the required employees
        for (const job of rows) {

            // Loop through the project job capacity of the jobs
            for (const project_job_capacity of job.project_job_capacities || []) {
                await saveData({
                    apiUrl: 'post_project_job_capacity',
                    data: {
                        project_id: project.id,
                        job_id: job.id,
                        date: project_job_capacity.date,
                        required_employees: project_job_capacity.required_employees || 0
                    }
                });
            }
        }

        setHasUnsavedChanges(false);
        setLoading(false);
    };

    // Handle create project job
    const handleCreateProjectJob = async () => {
        initializeModal(
            React.cloneElement(
                <ProjectJobForm 
                    linkedItem={{ 
                        project: project.id,
                        ...(project.client && { 
                            client: {
                                id: project.client.id,
                                name: project.client.name,
                            }
                        }),
                        ...(project.work_location && {
                            work_location: {
                                id: project.work_location.id,
                                address: project.work_location.address
                            }
                        }),
                        ...(project.location_person && {
                            location_person: {
                                id: project.location_person.id,
                                name: project.location_person.name
                            }
                        }),
                        plan_method: 'add_to_storage'
                    }}
                />
            ), {
                title: t('button.add_object_label', { object_name: t(`job.general.object_name.singular`) }),
                modalSize: 'add-form'
            } 
        );
    }

    // Handle open the job
    const handleOpenJob = (jobId: number | null) => {
        if (!jobId) return;
        initializeModal(
            React.cloneElement(<JobModal />), { 
                itemId: jobId,
                modalSize: 'large' 
            } 
        );
    }

    // Determine wheter the capacity input is disabled
    const isCapacityInputDisabled = (day: Date) => {
        const projectStart = project.start_date ? startOfDay(new Date(project.start_date)) : null;
        const projectEnd = project.end_date ? startOfDay(new Date(project.end_date)) : null;
        const currentDay = startOfDay(day);

        if (projectStart && currentDay < projectStart) return true
        if (projectEnd && currentDay > projectEnd) return true

        return false
    }

    // Get the number of days based on the scheduling display days setting 
    const days_mode = userPreferences.scheduling_display_mode;
    const numberOfDays = days_mode === 'mon-fri' ? 5 : days_mode === 'mon-sat' ? 6 : 7;

    // Generate the weekdays
    const weekDays = Array.from({ length: numberOfDays }).map((_, index) =>
        addDays(currentWeekStart, index)
    );

    // Generate the amount of columns
    const gridTemplate = `2fr repeat(${numberOfDays}, 1fr) 128px`;

    return (
        <div className='card project-jobs-card'>
            <div className='project-jobs-capacity'>
                <div className="planning-defaults">
                    <div className='planning-defaults-header'>
                        <p>
                            {t('project.general.project_planning_defaults_header')}
                        </p>
                        <span className="planning-defaults-tooltip tooltip-icon">
                            <FontAwesomeIcon icon={faCircleQuestion} />
                            <span className="tooltip tooltip-right">{t('project.general.project_planning_defaults_explanationText')}</span>
                        </span>
                    </div>
                    <div className='field-column'>
                        <div className='defaults-label'>
                            {t('project.general.default_start_time_label')}
                        </div>
                        <TimeInput
                            type="time"
                            name="default_start_time"
                            value={projectDefaults.default_start_time}
                            onChange={(value) => updateProjectDefault('default_start_time', value)}
                            disabled={!isEditable?.editability}
                        />
                    </div>
                    <div className='field-column'>
                        <div className='defaults-label'>
                            {t('project.general.default_end_time_label')}
                        </div>
                        <TimeInput
                            type="time"
                            name="default_end_time"
                            value={projectDefaults.default_end_time}
                            onChange={(value) => updateProjectDefault('default_end_time', value)}
                            disabled={!isEditable?.editability}
                        />
                    </div>
                    <div className='field-column'>
                        <div className='defaults-label'>
                            {t('project.general.default_break_duration_label')}
                        </div>
                        <TimeInput
                            type="time"
                            name="default_break_duration"
                            timeRange={{ from: "00:00", to: "02:00" }}
                            value={formatBreakDuration(projectDefaults.default_break_duration)}
                            onChange={(value) => updateProjectDefault('default_break_duration', value)}
                            disabled={!isEditable?.editability}
                        />
                    </div>
                </div>

                <div className='capacity-grid'>
                    <div className='navigation-header'>
                        <p>
                            {t('project.general.job_capacity_description')}
                        </p>
                        <WeekNavigation
                            weekStartDate={currentWeekStart}
                            buttonSize='small'
                            reverse={true}
                            numberOfDays={numberOfDays}
                            onChange={(newDate) => setCurrentWeekStart(startOfWeek(newDate, { weekStartsOn: 1 }))}
                        />
                    </div>

                    <div className="grid-header"
                            style={{ display: 'grid', gridTemplateColumns: gridTemplate }}
                    >
                        <div>{t('project.general.job_description_label')}</div>

                        {weekDays.map(day => (
                            <div key={day.toISOString()}
                                    className='day-label'>
                                {format(day, 'EEE dd', { locale: getDateFnsLocale(userLocale) })}
                            </div>
                        ))}

                        <div>{t('project.general.job_status_label')}</div>
                    </div>

                    {rows?.filter(row => !row.deleted).map(job => {
                        return (
                            <div key={job.id} 
                                className="grid-row"
                                style={{ display: 'grid', gridTemplateColumns: gridTemplate }}
                                onClick={() => handleOpenJob(job.id)}
                            >
                                <p>
                                    {job.main_description
                                        ? job.main_description.length > 24
                                            ? job.main_description.slice(0, 24) + ".."
                                            : job.main_description
                                        : "-"
                                    }
                                </p>

                                {weekDays.map(day => {
                                    // Get the date key
                                    const dateKey = format(day,'yyyy-MM-dd')

                                    // Get the job capacity for the date from the mapping
                                    const jobCapacity = projectJobCapacityMap.get(`${job.id}-${dateKey}`)

                                    // Determine wheter the input field is disabled
                                    const disabled = !isEditable?.editability || isCapacityInputDisabled(day);

                                    return (
                                        <input
                                            key={day.toISOString()}
                                            type="number"
                                            min={0}
                                            disabled={disabled}
                                            value={jobCapacity ?? ''}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => updateRequiredEmployees(day, e.target.value, job.id)}
                                        />
                                    )
                                })}   

                                <div className="badge-container">
                                    <div className={`status-badge ${job.status_condition}`}>
                                        {t(`workflow.default_status_names.${job.status_name}`, { defaultValue: job.status_name })}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {isEditable?.editability && !userHasOnlyViewRights && (
                        <div className='buttons-row'>
                            <div onClick={handleCreateProjectJob}
                                    className="add-new-button">
                                {t('project.general.add_new_sub_job')}
                            </div>
                            {hasUnsavedChanges && (
                                <div className='buttons-right'>
                                    <SecondaryButton
                                        onClick={() => setHasUnsavedChanges(false)}
                                        label='general.cancel'
                                        size='extra-small'
                                    />
                                    <PrimaryButton
                                        onClick={handleSave}
                                        label='general.save'
                                        loading={loading}
                                        size='extra-small'
                                    />
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </div>
    )
}

export default ProjectPlanningTab;